import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { logEvent, analytics, db } from '../firebase.js';
import { doc, onSnapshot } from 'firebase/firestore';
import { DesignService } from '../services/design-service.js';
import { QueryCache } from '../services/cache.js';
import { AIService } from '../services/ai-service.js';
import { gridImage, fallbackToFull } from '../utils/design-image.js';
import { BillingService } from '../services/billing-service.js';
import { compositeWatermark } from '../services/watermark.js';
import { shareLink, shareOrDownloadImage } from '../services/share-service.js';
import { publicOrigin } from '../services/platform-service.js';
import { buildShoppingUrl, shoppingProvider } from '../services/shopping-links.js';
import { EditRegionModal } from '../components/EditRegionModal.jsx';
import { PlanRoomPicker } from '../components/PlanRoomPicker.jsx';
import { PlanRoomResult, planSliderLabels, planPrefill, PLAN_ROOM_ENABLED } from '../components/PlanRoomResult.jsx';
import { useCreditConfig } from '../services/credit-config.js';
import { useCredits } from '../services/credits-service.js';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider.jsx';
import { Comments } from '../components/Comments.jsx';
import { SaveButton } from '../components/SaveButton.jsx';
import { CaptionEditor } from '../components/CaptionEditor.jsx';
import { useLocale, LANG_LABELS, randomRenamePlaceholderIndex } from '../hooks/useLocale.jsx';
import { useInAppBack } from '../hooks/useInAppBack.jsx';


// 이미지를 받아 디코드까지 해 둔다. 하나가 실패하거나 8초를 넘기면 기다리지 않는다.
function preloadImages(urls) {
  const one = (u) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => (img.decode ? img.decode().catch(() => {}) : Promise.resolve()).then(resolve);
    img.onerror = resolve;
    img.src = u;
  });
  return Promise.race([
    Promise.all(urls.filter(Boolean).map(one)),
    new Promise((r) => setTimeout(r, 8000)),
  ]);
}

export function DesignDetail({ user, openSignIn, onOutOfCredits, onGenerationStart, onGenerationDone, onGenerationError }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const back = useInAppBack();
  const { t, lang, locale, styleName } = useLocale();
  const creditCfg = useCreditConfig();
  const credits = useCredits();
  const [pickingRoom, setPickingRoom] = useState(false);
  // Hydrate from cache so navigating back to a recently-viewed design is instant.
  const cachedDesign = id ? QueryCache.get(`design:${id}`) : null;
  const [design, setDesign] = useState(cachedDesign || null);
  const [loading, setLoading] = useState(!cachedDesign);
  const [loadError, setLoadError] = useState(null);
  // 서버가 "아직 생성 중" 이라고 확인해 준 뒤에만 "만드는 중" 을 띄운다 (아래 onSnapshot).
  const [stillGenerating, setStillGenerating] = useState(false);
  const [actionError, setActionError] = useState(null);
  // 생성 직후 넘어온 경우에만 쓸기 연출. 보관함에서 다시 열 땐 이미 본
  // 결과라 바로 100% 다. mount 시 한 번만 읽어서, 뒤로/앞으로 왕복하며
  // 같은 history 엔트리로 돌아와도 다시 돌지 않게 한다.
  const location = useLocation();
  const [justGenerated] = useState(() => !!location.state?.justGenerated);
  const [renaming, setRenaming] = useState(false);
  // 모달을 열 때마다 placeholder 를 새로 뽑는다 — 하나로 고정해 두면
  // 다들 그 문구를 그대로 저장한다.
  const [placeholderIdx, setPlaceholderIdx] = useState(randomRenamePlaceholderIndex);
  const [nameInput, setNameInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [togglingListed, setTogglingListed] = useState(false);
  const [feedToast, setFeedToast] = useState(false);
  // captionModal: null | 'post' | 'edit'
  const [captionModal, setCaptionModal] = useState(null);
  const [plan, setPlan] = useState('free');
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  const [editPhotoIndex, setEditPhotoIndex] = useState(null);
  // Gio 의 편집 카드에서 "직접 칠하기" 로 오면 그 사진·바꿀 내용을 채운 채 연다.
  const [openEdit] = useState(() => location.state?.openEdit || null);
  const [editPrefill, setEditPrefill] = useState(null);
  const cachedEdits = id ? QueryCache.get(`editsOf:${id}`) : null;
  const [edits, setEdits] = useState(cachedEdits || []);

  const isOwner = user && design && user.uid === design.userId;
  const madeAt = design?.timestamp?.toDate?.() ?? (design?.timestamp ? new Date(design.timestamp) : null);
  const showLastFreeHint = !!isOwner && !user.isAnonymous
    && design.status === 'success'
    && madeAt && Date.now() - madeAt.getTime() < 3 * 60 * 60 * 1000
    && credits.plan !== 'pro' && !credits.loading && credits.credits !== null
    && credits.credits < creditCfg.price.generate;
  useEffect(() => {
    if (showLastFreeHint) logEvent(analytics, 'last_free_hint_shown');
  }, [showLastFreeHint, id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!openEdit || !isOwner) return;
    const idx = Math.max(0, Number(openEdit.photoIndex) || 0);
    if (!design.generatedImageUrls?.[idx]) return;
    setEditPrefill(openEdit.replacement || '');
    setEditPhotoIndex(idx);
    // 같은 history 엔트리로 돌아와도 다시 열리지 않게.
    navigate(location.pathname, { replace: true, state: {} });
  }, [openEdit, isOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || user.isAnonymous) {
      setPlan('free');
      return;
    }
    return BillingService.subscribeToSubscription(user.uid, (sub) => {
      setPlan(sub?.plan || 'free');
    });
  }, [user]);

  useEffect(() => {
    DesignService.getDesign(id)
      .then(d => {
        if (!d) { setLoadError(t('errDesignNotFound')); return; }
        setDesign(d);
        setNameInput(prev => prev || d.name || '');
        QueryCache.set(`design:${id}`, d);
      })
      .catch(() => { if (!cachedDesign) setLoadError(t('errLoadDesign')); })
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  // 서버 작업(2.3.0)이 아직 도는 중이면 끝날 때까지 지켜본다. 완료 푸시를 눌러
  // 들어오면 앱이 막 깨어난 참이라 getDoc 이 기기 캐시의 "생성 중" 사본(결과
  // 이미지 없음)을 줄 수 있다 — 그대로 두면 "Image unavailable" 로 굳었다.
  // 캐시 사본만 보고 "만드는 중" 을 띄우면 "완성됐어요" 알림을 누르고 들어온
  // 사람에게 틀린 말을 하게 된다 — 서버 응답이 올 때까지는 스피너만 둔다.
  useEffect(() => {
    if (design?.status !== 'generating') return undefined;
    setStillGenerating(false);
    let cancelled = false;
    const unsub = onSnapshot(doc(db, 'designs', id), { includeMetadataChanges: true }, (snap) => {
      if (!snap.exists()) return;
      const d = { id: snap.id, ...snap.data() };
      if (d.status === 'generating') {
        if (!snap.metadata.fromCache) setStillGenerating(true);
        return;
      }
      // 사진을 먼저 받아 두고 넘긴다 — 원본이 먼저 뜨고 디자인이 뒤늦게 차오르는
      // 대신 스피너가 걷히는 순간 둘이 같이 뜨게.
      preloadImages([...(d.generatedImageUrls || []), ...(d.originalImageUrls || [])]).then(() => {
        if (cancelled) return;
        setDesign((prev) => ({ ...prev, ...d }));
        QueryCache.set(`design:${id}`, d);
      });
    }, () => setStillGenerating(true));
    return () => { cancelled = true; unsub(); };
  }, [id, design?.status]);

  // Lineage: fetch designs that were edited from this one (only for owner —
  // the query is filtered by sourceDesignId on the server side, but a viewer
  // who doesn't own the source has no use for the edit list anyway).
  useEffect(() => {
    if (!design || !user || user.isAnonymous || design.userId !== user.uid) {
      setEdits([]);
      return;
    }
    DesignService.getEditsOfDesign(id).then(e => {
      setEdits(e);
      QueryCache.set(`editsOf:${id}`, e);
    }).catch(() => setEdits([]));
  }, [design, user, id]);

  const handleToggleFavorite = async () => {
    const next = await DesignService.toggleFavorite(id, design.isFavorite);
    setDesign(prev => {
      const updated = { ...prev, isFavorite: next };
      QueryCache.set(`design:${id}`, updated);
      return updated;
    });
    // MyDesigns favorites filter cache is now stale.
    QueryCache.delByPrefix('userDesigns:');
  };

  const handleRename = async () => {
    if (!nameInput.trim()) return;
    await DesignService.renameDesign(id, nameInput);
    setDesign(prev => {
      const updated = { ...prev, name: nameInput.trim() };
      QueryCache.set(`design:${id}`, updated);
      return updated;
    });
    QueryCache.delByPrefix('userDesigns:');
    setRenaming(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await DesignService.deleteDesign(design);
      QueryCache.del(`design:${id}`);
      QueryCache.delByPrefix('userDesigns:');
      navigate('/designs');
    } catch {
      setActionError(t('errDeleteDesign'));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const shareUrl = `${publicOrigin()}/s/${id}`;

  // 게시 버튼 클릭. unlisted → 캡션 모달 열기 (사용자가 글을 같이 올릴 수 있도록).
  // listed → 그냥 즉시 unlist (캡션은 그대로 보존, 재게시 시 prefill).
  const handleToggleListed = () => {
    if (!design.isListed) {
      setCaptionModal('post');
    } else {
      void runToggleListed(false);
    }
  };

  // 캡션 모달 submit 또는 unlist 시 실제 firestore 업데이트.
  const runToggleListed = async (next, caption = undefined) => {
    setTogglingListed(true);
    try {
      await DesignService.setListed(id, next, caption);
      if (next && !design.isPublic) await DesignService.setPublic(id, true);
      setDesign(prev => ({
        ...prev,
        isListed: next,
        ...(caption !== undefined && { caption }),
        ...(next && { isPublic: true }),
      }));
      if (next) {
        setFeedToast(true);
        setTimeout(() => setFeedToast(false), 2500);
        logEvent(analytics, 'design_posted_to_feed', { style: design.style });
      }
    } finally {
      setTogglingListed(false);
    }
  };

  // 게시된 디자인의 caption 만 수정 (편집 모달에서 호출).
  const runUpdateCaption = async (caption) => {
    await DesignService.updateCaption(id, caption);
    setDesign(prev => ({ ...prev, caption }));
  };

  // 친구에게 공유 — native share 시트가 copy URL / 메시지 / SNS 다 처리. 단
  // ShareView 가 isPublic 일 때만 보여주므로 자동으로 켜준다.
  const handleShare = async () => {
    try {
      if (!design.isPublic) {
        await DesignService.setPublic(id, true);
        setDesign(prev => ({ ...prev, isPublic: true }));
      }
      const shared = await shareLink({
        title: design.name || styleLabel,
        text: t('designShareText', { style: styleLabel }),
        url: shareUrl,
      });
      if (shared) {
        logEvent(analytics, 'design_shared', { method: 'native_share', style: design.style });
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.error(err);
    }
  };

  const handleDownload = async (url, index) => {
    setDownloadingIndex(index);
    try {
      // 서명은 전원에게 붙는다 — 게이팅이 아니라 브랜딩이라 조건이 없다
      // (src/services/watermark.js 상단 주석 참고).
      const blob = await compositeWatermark(url);
      const result = await shareOrDownloadImage({
        blob,
        filename: `archelier-design-${index + 1}.jpg`,
        title: design.name || styleLabel,
        text: `${styleLabel} — archelier`,
      });
      logEvent(analytics, 'design_downloaded', { plan, watermarked: true, method: result });
    } catch {
      window.open(url, '_blank');
    } finally {
      setDownloadingIndex(null);
    }
  };

  const handleReuseStyle = () => {
    const styleLabel = styleName(design.style, design.spaceCategory);
    // '/' 는 데스크탑에선 HomePage, 모바일에선 '/create' 로 redirect — 어느 쪽이든
    // 업로드 화면을 정확히 가리키려면 '/create' 를 직접 쓴다.
    const dest = window.matchMedia('(max-width: 768px)').matches ? '/create' : '/';
    navigate(dest, {
      state: {
        style: design.style,
        customStyle: design.customStyleInput || '',
        additionalRequest: design.additionalRequest || '',
        budget: design.budget || 'mid',
        mode: design.mode || 'redesign',
        spaceCategory: design.spaceCategory || 'interior',
        exteriorOptions: design.exteriorOptions || null,
        referenceImageUrl: design.referenceImageUrl || null,
        sourceName: design.name || styleLabel,
        ...planPrefill(design),
      }
    });
  };

  // 3D 평면도 위에 방을 칠해 그 방을 눈높이로 (2.4.0). 부분 편집처럼 창을 닫고 대기 배너로 넘긴다.
  // 원래 평면도 + 3D 평면도(참고) + 칠한 사본 세 장을 넘겨 같은 집처럼 보이게 한다.
  const startRoomFromPlan = async (blob) => {
    setPickingRoom(false);
    onGenerationStart?.();
    try {
      const roomOverlayUrl = await AIService.uploadEditOverlay(blob);
      const { designId } = await AIService.startDesignJob({
        originalImageUrls: [design.originalImageUrls[0]],
        style: design.style,
        customStyleDescription: design.customStyleInput || '',
        mode: 'plan_room',
        roomOverlayUrl,
        overlayOn3d: true,
        planReferenceUrl: design.generatedImageUrls[0],
        planSourceDesignId: design.id,
        budget: design.budget || 'mid',
        spaceCategory: 'interior',
        promptLang: locale.promptLang,
        lang,
      });
      const done = await AIService.waitForDesign(designId);
      if (done.status !== 'success') throw Object.assign(new Error('PLAN_ROOM_FAILED'), { code: 'PLAN_ROOM_FAILED' });
      logEvent(analytics, 'design_generated', { style: design.style, mode: 'plan_room', space_category: 'interior', photo_count: 1, path: 'plan_detail' });
      onGenerationDone?.(designId);
    } catch (err) {
      console.error('Plan room failed:', err);
      const noCredits = err.code === 'INSUFFICIENT_CREDITS' || err.status === 402;
      if (noCredits) {
        logEvent(analytics, 'credit_exhausted', { surface: 'plan_room' });
        onOutOfCredits?.();
      }
      onGenerationError?.(noCredits
        ? t('errOutOfCreditsToday', { n: Math.floor(creditCfg.daily / creditCfg.price.generate) })
        : t('generationFailedHint'));
    }
  };

  if (loading) return <main className="main"><div className="loading"><div className="spinner"></div><p>{t('loading')}</p></div></main>;
  if (loadError) return <main className="main"><div className="error-message">{loadError}</div><div className="controls"><button className="btn btn-secondary" onClick={() => navigate(-1)}>{t('back')}</button></div></main>;
  if (!design) return null;
  // 기기 캐시엔 "생성 중" 인데 서버 답이 아직이면 — 대개 완료 알림을 누르고 들어온
  // 경우다 — 반쪽짜리 화면 대신 스피너. 서버가 정말 생성 중이라고 하면 그때 페이지를 연다.
  if (design.status === 'generating' && !stillGenerating) {
    return <main className="main"><div className="loading"><div className="spinner"></div></div></main>;
  }

  const styleLabel = styleName(design.style, design.spaceCategory);
  const lineOnly = design.outputType === 'plan_clean';
  const photoCount = Math.max(
    design.originalImageUrls?.length || 0,
    design.generatedImageUrls?.length || 0,
    1
  );

  return (
    <main className="main">
      {actionError && (
        <div className="error-message">
          {actionError}
          <button onClick={() => setActionError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1rem', lineHeight: 1 }}>×</button>
        </div>
      )}
      {/* Top bar — back 버튼: 모바일은 MobileHeader 가 처리 (CSS hide), 데스크톱은 '← Back' 노출. */}
      <div className="detail-topbar">
        <div className="detail-topbar-row">
          {/* 웹에서 공유 링크로 바로 연 사람에겐 back 을 안 그린다 —
              useInAppBack 주석 참고. 앱에서는 항상 있다. */}
          {back.showBack ? (
            <button className="detail-back-btn" onClick={back.goBack} aria-label={t('back')}>
              <i className="material-symbols-rounded">arrow_back_ios_new</i>
              <span>{t('back')}</span>
            </button>
          ) : <span />}

          {isOwner && (
            <div className="detail-actions">
              <button
                className={`detail-icon-btn ${design.isFavorite ? 'fav-active' : ''}`}
                onClick={handleToggleFavorite}
                title={design.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <i className="material-symbols-rounded">favorite</i>
              </button>
              <SaveButton designId={id} user={user} variant="icon" />
              <button className="detail-icon-btn" onClick={() => { setPlaceholderIdx(randomRenamePlaceholderIndex()); setRenaming(true); }} title="Rename">
                <i className="material-symbols-rounded">edit</i>
              </button>
              <button
                className="detail-icon-btn"
                onClick={handleShare}
                title={t('share')}
              >
                <i className="material-symbols-rounded">ios_share</i>
              </button>
              {/* "피드에 올리기" 버튼은 감춰 뒀다 — 공개 피드 라우트를 접어서
                 (App.jsx 참고) 올려도 갈 곳이 없다. handleToggleListed /
                 DesignService.setListed 는 그대로 살아 있으니 피드를 되살릴 때
                 이 버튼만 복구하면 된다. 공유는 위의 ios_share 로 계속 가능. */}
              <button
                className={`detail-icon-btn ${downloadOpen ? 'is-on' : ''}`}
                onClick={() => setDownloadOpen(s => !s)}
                title={t('download')}
              >
                <i className="material-symbols-rounded">file_download</i>
              </button>
              <button className="detail-icon-btn danger-btn" onClick={() => setConfirmDelete(true)} title="Delete">
                <i className="material-symbols-rounded">delete</i>
              </button>
            </div>
          )}
        </div>

        <div className="detail-title">
          <h2>{design.name || styleLabel}</h2>
          <p>{DesignService.formatDate(design.timestamp, lang)}{photoCount > 1 && ` · ${t('photoCount', { n: photoCount })}`}</p>
          {(design.caption || (isOwner && design.isListed)) && (
            <p className="design-caption">
              {design.caption || (
                <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  {t('captionPlaceholder')}
                </span>
              )}
              {isOwner && design.isListed && (
                <button
                  type="button"
                  className="feed-card-caption-edit"
                  onClick={() => setCaptionModal('edit')}
                  title={t('captionEdit')}
                  aria-label={t('captionEdit')}
                >
                  <i className="material-symbols-rounded">edit</i>
                </button>
              )}
            </p>
          )}
        </div>
      </div>

      {design.sourceDesignId && (
        <div className="edited-from-banner">
          <i className="material-symbols-rounded">auto_fix_high</i>
          <span>{t('editedFromBanner')}</span>
          <button
            type="button"
            className="edited-from-link"
            onClick={() => navigate(`/designs/${design.sourceDesignId}`)}
          >
            {t('viewOriginal')}
            <i className="material-symbols-rounded">arrow_forward</i>
          </button>
        </div>
      )}

      {/* Moderation 자동 unlist 알림 — toolbar 아래 항상 표시 (panel 진입 안 해도 보임). */}
      {isOwner && design.moderationFlag && (
        <div className="moderation-notice">
          <i className="material-symbols-rounded">info</i>
          <span>{t('moderationAutoUnlisted')}</span>
        </div>
      )}

      {/* Download panel — 다운로드 버튼 클릭 시에만 펼침. share / feed 토글은
         toolbar 의 별도 아이콘 버튼이 처리. */}
      {downloadOpen && isOwner && (
        <div className="share-panel">
          {design.generatedImageUrls?.length > 0 && (
            <div className="share-panel-download-row">
              <p className="share-panel-label">{t('downloadImages')}</p>
              <div className="share-panel-download-btns">
                {design.generatedImageUrls.map((url, i) => (
                  <button
                    key={i}
                    className="btn btn-secondary"
                    onClick={() => handleDownload(url, i)}
                    disabled={downloadingIndex === i}
                  >
                    <i className="material-symbols-rounded">download</i>
                    {design.generatedImageUrls.length > 1 ? t('photoLabel', { n: i + 1 }) : t('download')}
                  </button>
                ))}
              </div>
              <p className="share-panel-hint download-watermark-hint">
                <i className="material-symbols-rounded">info</i>
                {t('downloadWatermarkHint')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Rename modal */}
      {renaming && (
        <div className="modal-overlay" onClick={() => setRenaming(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>{t('renameTitle')}</h3>
            <input
              className="rename-input"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              placeholder={t(`renamePlaceholders.${placeholderIdx}`)}
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenaming(false); }}
            />
            <div className="controls" style={{ marginTop: '1rem' }}>
              <button className="btn btn-primary" onClick={handleRename}>{t('save')}</button>
              <button className="btn btn-secondary" onClick={() => setRenaming(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>{t('deleteTitle')}</h3>
            <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
              {t('deleteDesc')}
            </p>
            <div className="controls" style={{ marginTop: '1.5rem' }}>
              <button className="btn btn-primary" style={{ background: 'var(--error)', borderColor: 'var(--error)' }} onClick={handleDelete} disabled={deleting}>
                {deleting ? t('deleting') : t('delete')}
              </button>
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Before/After pairs */}
      {/* 흑백 도면 정리 결과엔 팔레트·가구·조명이 의미가 없다 — 아래 분석에서 뺀다. */}
      {Array.from({ length: photoCount }, (_, i) => {
        const before = design.originalImageUrls?.[i];
        const after = design.generatedImageUrls?.[i];
        const labels = planSliderLabels(t, design.outputType);
        return (
          <div key={i} className="result-photo-pair">
            {photoCount > 1 && <h3 className="result-photo-label">{t('photoLabel', { n: i + 1 })}</h3>}
            {before && after ? (
              <>
                {/* 쓸기는 "처음 보는 화면" 에서만. 보관함에서 다시 열면 이미 본
                    결과라 바로 100% 이고, 생성 직후 넘어온 경우만 연출한다.
                    로그인 사용자는 ResultStep 을 거치지 않고 여기로 바로
                    오므로(App.jsx 의 자동 이동) 이 분기가 없으면 생성하고도
                    연출을 전혀 못 본다. */}
                {design.outputType === 'plan_room' ? (
                  <PlanRoomResult src={after} planSrc={design.roomOverlayUrl || before} alt={t('altGenerated', { n: i + 1 })} />
                ) : (
                  <BeforeAfterSlider
                    beforeSrc={before}
                    afterSrc={after}
                    beforeLabel={labels.before}
                    afterLabel={labels.after}
                    alt={t('altPhoto', { n: i + 1 })}
                    revealOnMount={justGenerated}
                  />
                )}
                {isOwner && (
                  <div className="controls" style={{ justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                    {PLAN_ROOM_ENABLED && design.outputType === 'plan_3d' && (
                      <button className="btn btn-primary edit-region-btn" onClick={() => setPickingRoom(true)}>
                        <i className="material-symbols-rounded">meeting_room</i>
                        {t('planRoomFromResult')}
                      </button>
                    )}
                    <button
                      className="btn btn-secondary edit-region-btn"
                      onClick={() => setEditPhotoIndex(i)}
                    >
                      <i className="material-symbols-rounded">auto_fix_high</i>
                      {t('editRegion')}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="image-comparison">
                <div className="image-container">
                  <h3>{t('original')}</h3>
                  {before ? <img src={before} alt={t('altOriginal', { n: i + 1 })} className="preview-image" /> : <div className="image-error"><p>{t('imageUnavailable')}</p></div>}
                </div>
                <div className="image-container">
                  <h3>{t('aiDesign')}</h3>
                  {after ? <img src={after} alt={t('altGenerated', { n: i + 1 })} className="preview-image" />
                    : design.status === 'generating'
                      ? <div className="image-generating"><div className="spinner" /><p>{t(design.sourceDesignId ? 'gioJobEditing' : 'gioJobMaking')}</p></div>
                    : <div className="image-error"><p>{t('imageUnavailable')}</p></div>}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* 오늘 무료를 다 쓴 직후 한 줄 — 결과를 보고 "하나 더" 하고 싶은 순간이 팩을
          가장 자연스럽게 보여줄 자리다. 상시 배너가 아니다: 내 디자인 · 방금(3시간 안)
          만든 것 · 생성 1장 값도 안 남았을 때만 (MONETIZATION.md ① 실행). */}
      {showLastFreeHint && (
        <button
          type="button"
          className="last-free-hint"
          onClick={() => { logEvent(analytics, 'last_free_hint_tap'); onOutOfCredits?.(); }}
        >
          <i className="material-symbols-rounded">bolt</i>
          <span className="last-free-hint-text">
            {t('lastFreeHint', { n: Math.floor(creditCfg.daily / creditCfg.price.generate) })}
          </span>
          <span className="last-free-hint-cta">{t('lastFreeHintCta')}</span>
        </button>
      )}

      {/* Style used */}
      <div className="preview-container">
        <h3>{t('styleUsed')}</h3>
        <div style={{ marginTop: '0.75rem' }}>
          <div className="style-tags">
            <button
              type="button"
              className="style-tag style-tag-link"
              onClick={() => navigate(`/feed?style=${encodeURIComponent(design.style)}${design.spaceCategory ? `&category=${design.spaceCategory}` : ''}`)}
              title={t('viewFeed')}
            >
              {styleLabel}
            </button>
            {design.spaceCategory && design.spaceCategory !== 'interior' && (
              <button
                type="button"
                className="style-tag style-tag-link"
                onClick={() => navigate(`/feed?category=${design.spaceCategory}`)}
                title={t('viewFeed')}
              >
                {t('spaceCategories.' + design.spaceCategory)}
              </button>
            )}
            {design.outputType && (
              <span className="style-tag">{t(`planOutput_${design.outputType}`)}</span>
            )}
            {design.mode === 'staging' && design.detectedInputType !== 'floorplan' && (
              <span className="style-tag">{t('modes.staging')}</span>
            )}
            {design.detectedInputType && design.detectedInputType !== 'photo' && !design.outputType && (
              <span className="style-tag">{t('detectedInputTypes.' + design.detectedInputType)}</span>
            )}
            {design.exteriorOptions?.season && (
              <span className="style-tag">{t('seasons.' + design.exteriorOptions.season)}</span>
            )}
            {design.exteriorOptions?.timeOfDay && (
              <span className="style-tag">{t('timesOfDay.' + design.exteriorOptions.timeOfDay)}</span>
            )}
          </div>
          {design.customStyleInput && (
            <div style={{ marginTop: '0.75rem' }}>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{t('customDescLabel')}</p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{design.customStyleInput}</p>
            </div>
          )}
          {design.additionalRequest && (
            <div style={{ marginTop: '0.75rem' }}>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{t('additionalReqLabel')}</p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{design.additionalRequest}</p>
            </div>
          )}
        </div>
        <div className="controls" style={{ marginTop: '1.5rem', justifyContent: 'flex-start' }}>
          <button className="btn btn-secondary" onClick={handleReuseStyle}>
            <i className="material-symbols-rounded">replay</i>
            {t('useStyleAgain')}
          </button>
        </div>
      </div>

      {/* Lang mismatch notice */}
      {design.lang && design.lang !== lang && (
        <div className="lang-notice">
          <i className="material-symbols-rounded">info</i>
          <span>{t('langMismatchNotice', { lang: LANG_LABELS[design.lang] || design.lang })}</span>
        </div>
      )}

      {/* Design analysis */}
      <div className="preview-container">
        <h3>{t('analysisTitle')}</h3>

        {design.spaceAnalysis && (
          <div><h4>{t('spaceAnalysis')}</h4><div className="md-content"><ReactMarkdown>{design.spaceAnalysis}</ReactMarkdown></div></div>
        )}
        {design.styleDescription && (
          <div><h4>{t('styleDescription')}</h4><div className="md-content"><ReactMarkdown>{design.styleDescription}</ReactMarkdown></div></div>
        )}
        {design.colorPalette && !lineOnly && (
          <div>
            <h4>{t('colorPalette')}</h4>
            <div className="color-palette">
              {design.colorPalette.map((color, i) => (
                <div key={i} className="color-swatch-wrapper">
                  <div className="color-swatch" style={{ backgroundColor: color }} title={color} />
                  <span className="color-swatch-code">{color}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {design.furnitureRecommendations && !lineOnly && (
          <div>
            <h4>{t('furnitureRec')}</h4>
            <div className="recommendations-list md-content">
              <ul>
                {design.furnitureRecommendations.map((item, i) => {
                  const shopUrl = buildShoppingUrl(item.searchQuery || item.item, lang);
                  return (
                    <li key={i}>
                      <strong>{item.item}</strong>
                      {shopUrl && (
                        <a
                          className="shop-link"
                          href={shopUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={t('shopFor', { item: item.item })}
                          onClick={() => logEvent(analytics, 'furniture_shop_clicked', { provider: shoppingProvider(lang) })}
                        >
                          <i className="material-symbols-rounded">shopping_bag</i>
                          <span>{t('shopShort')}</span>
                        </a>
                      )}
                      : <ReactMarkdown components={{ p: ({children}) => <>{children}</> }}>{item.description}</ReactMarkdown>
                      {item.placement && <> (<ReactMarkdown components={{ p: ({children}) => <>{children}</> }}>{item.placement}</ReactMarkdown>)</>}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
        {design.lightingPlan && !lineOnly && (
          <div><h4>{t('lightingPlan')}</h4><div className="md-content"><ReactMarkdown>{design.lightingPlan}</ReactMarkdown></div></div>
        )}
        {design.tips && !lineOnly && (
          <div>
            <h4>{t('designTips')}</h4>
            <div className="recommendations-list md-content">
              <ul>{design.tips.map((tip, i) => <li key={i}><ReactMarkdown>{tip}</ReactMarkdown></li>)}</ul>
            </div>
          </div>
        )}
      </div>

      {/* 2.3.0 — 디자인 아래 채팅은 Gio 탭으로 옮겼다. 이 디자인을 첨부한 채로 연다. */}
      {isOwner && (
        <div className="preview-container gio-ask-section">
          <button
            type="button"
            className="btn btn-secondary gio-ask-btn"
            onClick={() => navigate('/gio', { state: { attachDesignId: id } })}
          >
            <span className="gio-monogram gio-monogram--sm" aria-hidden="true">G</span>
            {t('gioAskAboutDesign')}
          </button>
        </div>
      )}

      <Comments
        designId={id}
        designOwnerId={design.userId}
        user={user}
        onSignInRequest={openSignIn}
      />

      {edits.length > 0 && (
        <div className="preview-container edits-section">
          <h3>{t('editsOfThisDesign')}</h3>
          <p className="edits-section-desc">{t('editsOfThisDesignDesc')}</p>
          <div className="edits-grid">
            {edits.map(e => {
              const thumb = gridImage(e);
              return (
                <button
                  key={e.id}
                  type="button"
                  className="edit-thumb-card"
                  onClick={() => navigate(`/designs/${e.id}`)}
                >
                  {thumb && <img src={thumb} alt={e.name || 'Edit'} className="edit-thumb-img" loading="lazy" onError={fallbackToFull(e)} />}
                  <div className="edit-thumb-meta">
                    <p className="edit-thumb-title">{e.name || styleLabel}</p>
                    {e.additionalRequest && (
                      <p className="edit-thumb-desc">{e.additionalRequest}</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {pickingRoom && design.generatedImageUrls?.[0] && (
        <PlanRoomPicker
          src={design.generatedImageUrls[0]}
          remote
          submitLabel={t('planRoomMake')}
          meta={plan !== 'pro' ? t('planRoomMeta', { n: creditCfg.price.generate }) : null}
          onClose={() => setPickingRoom(false)}
          onSubmit={startRoomFromPlan}
        />
      )}
      {editPhotoIndex !== null && (
        <EditRegionModal
          design={design}
          photoIndex={editPhotoIndex}
          user={user}
          initialReplacement={editPrefill || ''}
          onClose={() => { setEditPhotoIndex(null); setEditPrefill(null); }}
          onPendingStart={onGenerationStart}
          onPendingDone={onGenerationDone}
          onPendingError={onGenerationError}
          onOutOfCredits={onOutOfCredits}
        />
      )}
      {captionModal && (
        <CaptionEditor
          mode={captionModal}
          initial={design.caption || ''}
          onClose={() => setCaptionModal(null)}
          onSubmit={(caption) =>
            captionModal === 'edit'
              ? runUpdateCaption(caption)
              : runToggleListed(true, caption)
          }
        />
      )}
      {feedToast && (
        <button
          type="button"
          className="toast-snackbar"
          onClick={() => setFeedToast(false)}
          aria-live="polite"
        >
          <i className="material-symbols-rounded">check_circle</i>
          <span>{t('postedToFeedTitle')}</span>
        </button>
      )}
    </main>
  );
}
